import { btnPrimary, btnSecondary } from '@/components/record-sheet/styles'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '@/components/ui/dialog'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import {
  type TemplateCard,
  type WorkflowDetail,
  addFromTemplate,
  fetchTemplates
} from '@/lib/workflows'
import { useQuery } from '@tanstack/react-query'
import {
  Check,
  CircleDollarSign,
  LayoutGrid,
  Loader2,
  type LucideIcon,
  Package,
  Scale,
  ShieldCheck,
  Sparkles,
  Store,
  TrendingUp,
  Zap
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AgentAvatar } from '../AgentAvatar'

const CATEGORY_ICON: Record<string, LucideIcon> = {
  'Trust & Safety': ShieldCheck,
  Money: CircleDollarSign,
  Merchants: Store,
  'Orders & bookings': Package,
  Growth: TrendingUp,
  'Legal & admin': Scale
}

const STARTERS = 'Start here'
const ALL = 'All'

function ConnectorChip({ c }: { c: TemplateCard['connectors'][number] }) {
  return (
    <span
      title={`${c.use}${c.ready ? '' : ` · Needs setup${c.setup ? `: ${c.setup}` : ''}`}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
    >
      <span className={cn('size-1.5 rounded-full', c.ready ? 'bg-emerald-500' : 'bg-amber-500')} />
      {c.name}
    </span>
  )
}

function Agents({ agents, size = 'xs' }: { agents: TemplateCard['agents']; size?: 'xs' | 'sm' }) {
  return (
    <div className="flex -space-x-1.5">
      {agents.map((a) => (
        <AgentAvatar
          key={a.key}
          agentKey={a.key}
          name={a.name}
          size={size}
          className="ring-2 ring-white dark:ring-neutral-900"
        />
      ))}
    </div>
  )
}

/** One template: what it does, when it runs, who works on it and what it plugs into. */
// Each category gets a colour of its own: red for risk & rules, green for money & growth.
const RED = {
  card: 'hover:border-red-300 dark:hover:border-red-500/40 dark:hover:shadow-red-950/40',
  glow: 'bg-red-500/15 dark:bg-red-500/20',
  icon: 'from-red-400 to-red-700 shadow-red-900/30',
  label: 'text-red-600 dark:text-red-400'
}
const GREEN = {
  card: 'hover:border-emerald-300 dark:hover:border-emerald-500/40 dark:hover:shadow-emerald-950/40',
  glow: 'bg-emerald-500/15 dark:bg-emerald-500/20',
  icon: 'from-emerald-400 to-emerald-700 shadow-emerald-900/30',
  label: 'text-emerald-600 dark:text-emerald-400'
}
function toneFor(category: string) {
  return /trust|safety|legal|order|booking/i.test(category) ? RED : GREEN
}

function Card({ t, onOpen }: { t: TemplateCard; onOpen: () => void }) {
  const Icon = CATEGORY_ICON[t.category] ?? LayoutGrid
  const missing = t.connectors.filter((c) => !c.ready).length
  const tone = toneFor(t.category)
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`xo-tile group relative flex h-full flex-col gap-3 overflow-hidden rounded-[36px] border border-neutral-200 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 dark:border-white/10 dark:bg-neutral-950 ${tone.card}`}
    >
      {/* Category glow in the corner, brighter on hover. */}
      <span
        aria-hidden
        className={`pointer-events-none absolute -right-16 -top-20 size-56 rounded-full blur-3xl opacity-60 transition-opacity duration-300 group-hover:opacity-100 ${tone.glow}`}
      />
      <div className="relative flex items-start gap-3">
        <span
          className={`flex size-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-white shadow-lg ${tone.icon}`}
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-[11px] font-semibold uppercase tracking-wider ${tone.label}`}>{t.category}</p>
          <p className="font-bold leading-snug text-neutral-900 dark:text-white">{t.name}</p>
        </div>
      </div>
      <p className="relative line-clamp-2 text-sm text-neutral-600 dark:text-neutral-300">{t.summary}</p>
      <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 dark:text-neutral-400">
        <Zap className="size-3.5 shrink-0" />
        <span className="truncate">{t.trigger}</span>
      </p>
      <div className="relative mt-auto space-y-2.5 border-t border-neutral-100 pt-3 dark:border-white/10">
        <div className="flex items-center gap-2">
          <Agents agents={t.agents} />
          <span className="min-w-0 truncate text-xs text-neutral-500 dark:text-neutral-400">
            {t.agents.map((a) => a.name).join(', ')}
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          {t.connectors
            .filter((c) => c.key !== 'xoblack')
            .map((c) => (
              <ConnectorChip key={c.key} c={c} />
            ))}
        </div>
        {missing > 0 && (
          <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-400">
            {missing} service{missing === 1 ? '' : 's'} to set up first
          </p>
        )}
      </div>
    </button>
  )
}

/** The template opened: every detail in plain words, its questions, and Add. */
function TemplateDialog({
  t,
  room,
  onClose,
  onAdded
}: {
  t: TemplateCard
  room?: number
  onClose: () => void
  onAdded: (wf: WorkflowDetail) => void
}) {
  const Icon = CATEGORY_ICON[t.category] ?? LayoutGrid
  const notReady = t.connectors.filter((c) => !c.ready)
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries(t.questions.map((q) => [q.id, q.options[0]?.label ?? '']))
  )
  const [enable, setEnable] = useState(notReady.length === 0)
  const [adding, setAdding] = useState(false)

  async function add() {
    setAdding(true)
    try {
      const wf = await addFromTemplate(t.key, { answers, room, enable })
      queryClient.setQueryData(['workflow', wf.id], wf)
      await queryClient.invalidateQueries({ queryKey: ['workflows'] })
      toast.success(
        wf.enabled ? `"${wf.name}" is on` : `"${wf.name}" added. Switch it on when you're ready.`
      )
      onAdded(wf)
    } catch (err) {
      notifyError('Could not add it', err)
    } finally {
      setAdding(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto rounded-[36px] border border-neutral-200 bg-white p-6 sm:max-w-2xl dark:border-neutral-800 dark:bg-neutral-950">
        <div className="flex items-start gap-3 pr-6">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white dark:bg-white dark:text-black">
            <Icon className="size-5" />
          </span>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              {t.category} · {t.steps} steps
            </p>
            <DialogTitle className="text-xl font-bold text-neutral-900 dark:text-white">{t.name}</DialogTitle>
            <DialogDescription className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-300">
              {t.summary}
            </DialogDescription>
          </div>
        </div>

        <section className="space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            How it works
          </h4>
          <p className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-700 dark:bg-neutral-900 dark:text-neutral-200">
            <Zap className="size-3.5" /> {t.trigger}
          </p>
          <ol className="space-y-2">
            {t.how.map((line, i) => (
              <li key={line} className="flex gap-3 text-sm text-neutral-800 dark:text-neutral-200">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-xs font-bold tabular-nums text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                  {i + 1}
                </span>
                <span className="pt-0.5">{line}</span>
              </li>
            ))}
          </ol>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Anything that changes data or sends a message asks you first, as a card in the chat.
          </p>
        </section>

        <div className="grid gap-4 sm:grid-cols-2">
          <section className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Who does the work
            </h4>
            <ul className="space-y-1.5">
              {t.agents.map((a) => (
                <li key={a.key} className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
                  <AgentAvatar agentKey={a.key} name={a.name} size="sm" />
                  {a.name}
                </li>
              ))}
            </ul>
          </section>
          <section className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              What it plugs into
            </h4>
            <ul className="space-y-1.5">
              {t.connectors.map((c) => (
                <li key={c.key} className="flex items-start gap-2">
                  <span
                    className={cn(
                      'mt-1.5 size-2 shrink-0 rounded-full',
                      c.ready ? 'bg-emerald-500' : 'bg-amber-500'
                    )}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                      {c.name}
                      <span
                        className={cn(
                          'ml-1.5 text-[11px] font-semibold',
                          c.ready ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'
                        )}
                      >
                        {c.ready ? 'Ready' : 'Needs setup'}
                      </span>
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {c.ready ? c.use : c.setup || c.use}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {t.questions.length > 0 && (
          <section className="space-y-3 rounded-[24px] bg-neutral-100 p-4 dark:bg-neutral-900">
            <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Make it yours
            </h4>
            {t.questions.map((q) => (
              <fieldset key={q.id}>
                <legend className="text-sm font-bold text-neutral-900 dark:text-white">{q.question}</legend>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {q.options.map((o) => {
                    const on = answers[q.id] === o.label
                    return (
                      <button
                        key={o.label}
                        type="button"
                        aria-pressed={on}
                        title={o.hint || undefined}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: o.label }))}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-all active:scale-95',
                          on
                            ? 'border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-black'
                            : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-200 dark:hover:border-neutral-500'
                        )}
                      >
                        {on && <Check className="size-3.5" />}
                        {o.label}
                      </button>
                    )
                  })}
                </div>
                {q.options.find((o) => o.label === answers[q.id])?.hint && (
                  <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                    {q.options.find((o) => o.label === answers[q.id])?.hint}
                  </p>
                )}
              </fieldset>
            ))}
          </section>
        )}

        {t.note && <p className="text-xs text-neutral-500 dark:text-neutral-400">{t.note}</p>}

        <div className="flex flex-wrap items-center gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-neutral-800 dark:text-neutral-200">
            <input
              type="checkbox"
              className="size-4 accent-neutral-900 dark:accent-white"
              checked={enable}
              onChange={(e) => setEnable(e.target.checked)}
            />
            Switch it on now
          </label>
          {notReady.length > 0 && enable && (
            <span className="text-xs text-amber-700 dark:text-amber-400">
              {notReady.map((c) => c.name).join(', ')} still need setup; those steps will fail until then.
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" className={btnSecondary} onClick={onClose}>
              Cancel
            </button>
            <button type="button" className={btnPrimary} onClick={add} disabled={adding}>
              {adding ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
              Add workflow
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Ready-made workflows by category. Pick one, answer a question or two, and it's on the canvas. */
export function TemplateGallery({
  room,
  onAdded,
  limit
}: {
  room?: number
  onAdded: (wf: WorkflowDetail) => void
  /** Show only this many cards, with a link to the rest (for tight spaces like an empty chat). */
  limit?: number
}) {
  const list = useQuery({ queryKey: ['workflow-templates'], queryFn: fetchTemplates, staleTime: 60_000 })
  const [category, setCategory] = useState(STARTERS)
  const [open, setOpen] = useState<TemplateCard | null>(null)
  const [all, setAll] = useState(false)

  const shown = useMemo(() => {
    const items = list.data?.templates ?? []
    const picked =
      category === ALL ? items : category === STARTERS ? items.filter((t) => t.starter) : items.filter((t) => t.category === category)
    return limit && !all ? picked.slice(0, limit) : picked
  }, [list.data, category, limit, all])

  if (list.error)
    return (
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        Templates couldn't load.{' '}
        <button type="button" className="font-semibold underline" onClick={() => list.refetch()}>
          Try again
        </button>
      </p>
    )

  const tabs = [STARTERS, ...(list.data?.categories ?? []), ALL]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {tabs.map((c) => {
          const Icon = c === STARTERS ? Sparkles : c === ALL ? LayoutGrid : (CATEGORY_ICON[c] ?? LayoutGrid)
          return (
            <button
              key={c}
              type="button"
              aria-pressed={category === c}
              onClick={() => setCategory(c)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                category === c
                  ? 'bg-neutral-900 text-white dark:bg-white dark:text-black'
                  : 'bg-neutral-100 text-neutral-600 hover:text-neutral-900 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:text-white'
              )}
            >
              <Icon className="size-3.5" />
              {c}
            </button>
          )
        })}
      </div>

      {list.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-64 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <p className="rounded-[36px] border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
          No templates here yet.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((t) => (
            <Card key={t.key} t={t} onOpen={() => setOpen(t)} />
          ))}
        </div>
      )}

      {limit && !all && (list.data?.templates.length ?? 0) > limit && (
        <button type="button" className={btnSecondary} onClick={() => setAll(true)}>
          <LayoutGrid className="size-4" /> See all {list.data?.templates.length} templates
        </button>
      )}

      {open && (
        <TemplateDialog
          t={open}
          room={room}
          onClose={() => setOpen(null)}
          onAdded={(wf) => {
            setOpen(null)
            onAdded(wf)
          }}
        />
      )}
    </div>
  )
}

/** The gallery in its own window, for opening from the chat. */
export function TemplatePicker({
  open,
  onOpenChange,
  room,
  onAdded
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  room?: number
  onAdded: (wf: WorkflowDetail) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto rounded-[36px] border border-neutral-200 bg-neutral-50 p-6 sm:max-w-5xl dark:border-neutral-800 dark:bg-black">
        <div>
          <DialogTitle className="text-xl font-bold text-neutral-900 dark:text-white">
            Ready-made workflows
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-500 dark:text-neutral-400">
            Pick one, answer a question or two, and it's ready. Change anything later in the chat.
          </DialogDescription>
        </div>
        <TemplateGallery
          room={room}
          onAdded={(wf) => {
            onOpenChange(false)
            onAdded(wf)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
